import React from "react";
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  AccountBalanceWallet,
  PersonOutline,
  QrCodeScanner,
  Settings,
} from "@mui/icons-material";
import useThemeStore from "@/shared/stores/useThemeStore";

type MoreAction = {
  key: string;
  label: string;
  onClick: () => void;
};

type EmbeddedMoreSheetProps = {
  open: boolean;
  onClose: () => void;
  actions: MoreAction[];
};

const ICON_BY_KEY: Record<string, JSX.Element> = {
  wallet: <AccountBalanceWallet fontSize="small" />,
  profile: <PersonOutline fontSize="small" />,
  scanQr: <QrCodeScanner fontSize="small" />,
  settings: <Settings fontSize="small" />,
};

const EmbeddedMoreSheet: React.FC<EmbeddedMoreSheetProps> = ({
  open,
  onClose,
  actions,
}) => {
  const { darkMode: isDarkMode } = useThemeStore();

  const surface = isDarkMode ? "var(--neutral-900)" : "#fff";
  const border = isDarkMode ? "var(--neutral-800)" : "#E5E7EB";
  const textPrimary = isDarkMode ? "#E5E7EB" : "#111827";
  const textMuted = isDarkMode ? "#9CA3AF" : "rgba(17, 24, 39, 0.55)";
  const handleBg = isDarkMode ? "rgba(229, 231, 235, 0.22)" : "rgba(17, 24, 39, 0.12)";

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        "& .MuiBackdrop-root": {
          bgcolor: isDarkMode ? "rgba(0, 0, 0, 0.65)" : "rgba(15, 23, 42, 0.4)",
        },
        "& .MuiDrawer-paper": {
          bgcolor: surface,
          color: textPrimary,
          backgroundImage: "none",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          overflow: "hidden",
          borderTop: `1px solid ${border}`,
          borderLeft: `1px solid ${border}`,
          borderRight: `1px solid ${border}`,
          borderBottom: "none",
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: "calc(10px + env(safe-area-inset-bottom))",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 4,
            borderRadius: 999,
            mx: "auto",
            mb: 1.5,
            bgcolor: handleBg,
          }}
        />
        <Typography sx={{ fontWeight: 700, mb: 1, color: textPrimary }}>
          More
        </Typography>
        <Divider sx={{ borderColor: border }} />
        <List disablePadding>
          {actions.map((action, idx) => (
            <React.Fragment key={action.key}>
              <ListItemButton
                onClick={() => {
                  onClose();
                  action.onClick();
                }}
                sx={{
                  py: 1.2,
                  borderRadius: 1.5,
                  mt: 0.5,
                  color: textPrimary,
                  "&:hover": {
                    bgcolor: isDarkMode
                      ? "rgba(255, 255, 255, 0.06)"
                      : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 34, color: textMuted }}>
                  {ICON_BY_KEY[action.key] ?? <Settings fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={action.label}
                  primaryTypographyProps={{
                    sx: { color: textPrimary, fontWeight: 500 },
                  }}
                />
              </ListItemButton>
              {idx < actions.length - 1 && (
                <Divider component="li" sx={{ borderColor: border }} />
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default EmbeddedMoreSheet;
